import { useMemo, useRef, useState } from "react";
import { Play, Search, Star } from "lucide-react";
import type { Channel } from "../../shared/types";
import { getVirtualWindow } from "../lib/virtual-list";

interface ChannelListProps {
  channels: Channel[];
  query: string;
  selectedId?: string;
  favoriteIds: ReadonlySet<string>;
  favoriteCount: number;
  favoritesOnly: boolean;
  loading: boolean;
  onQueryChange: (query: string) => void;
  onFavoritesOnlyChange: (favoritesOnly: boolean) => void;
  onToggleFavorite: (channelId: string) => void;
  onSelect: (channel: Channel) => void;
}

export function ChannelList({
  channels,
  query,
  selectedId,
  favoriteIds,
  favoriteCount,
  favoritesOnly,
  loading,
  onQueryChange,
  onFavoritesOnlyChange,
  onToggleFavorite,
  onSelect
}: ChannelListProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(520);
  const rowHeight = 66;
  const window = useMemo(
    () =>
      getVirtualWindow({
        totalItems: channels.length,
        scrollTop,
        viewportHeight,
        rowHeight,
        overscan: 6
      }),
    [channels.length, scrollTop, viewportHeight]
  );
  const visibleChannels = channels.slice(window.startIndex, window.endIndex);

  const measureViewport = () => {
    const height = scrollRef.current?.clientHeight;
    if (height && height !== viewportHeight) {
      setViewportHeight(height);
    }
  };

  return (
    <div className="channel-list">
      <label className="search-field">
        <Search aria-hidden="true" size={16} />
        <input
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder="Search channels"
          autoComplete="off"
        />
      </label>

      <div className="channel-controls" aria-label="Channel filters">
        <button
          type="button"
          className={`filter-button ${!favoritesOnly ? "is-active" : ""}`}
          onClick={() => onFavoritesOnlyChange(false)}
        >
          All
        </button>
        <button
          type="button"
          className={`filter-button ${favoritesOnly ? "is-active" : ""}`}
          onClick={() => onFavoritesOnlyChange(true)}
        >
          <Star aria-hidden="true" size={13} />
          Favorites
        </button>
      </div>

      <div className="channel-count">
        {loading
          ? "Loading"
          : `${channels.length.toLocaleString()} shown / ${favoriteCount.toLocaleString()} favorite${
              favoriteCount === 1 ? "" : "s"
            }`}
      </div>

      <div
        className="channel-scroll"
        ref={scrollRef}
        onScroll={(event) => {
          setScrollTop(event.currentTarget.scrollTop);
          measureViewport();
        }}
        onMouseEnter={measureViewport}
      >
        <div className="virtual-spacer" style={{ height: `${window.totalHeight}px` }}>
          <div className="virtual-window" style={{ transform: `translateY(${window.offsetTop}px)` }}>
            {!loading && visibleChannels.length === 0 ? (
              <div className="empty-channel-list">{favoritesOnly ? "No favorite channels" : "No channels found"}</div>
            ) : null}
            {visibleChannels.map((channel) => {
              const isFavorite = favoriteIds.has(channel.id);

              return (
                <div
                  key={channel.id}
                  role="button"
                  tabIndex={0}
                  className={`channel-row ${channel.id === selectedId ? "is-selected" : ""}`}
                  style={{ height: `${rowHeight - 8}px` }}
                  onClick={() => onSelect(channel)}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter" && event.key !== " ") {
                      return;
                    }

                    event.preventDefault();
                    onSelect(channel);
                  }}
                >
                  <span className="logo-box">
                    {channel.logo ? <img src={channel.logo} alt="" loading="lazy" /> : channel.name.slice(0, 2).toUpperCase()}
                  </span>
                  <span className="channel-copy">
                    <span className="channel-name">{channel.name}</span>
                    <span className="channel-group">{channel.group ?? "Ungrouped"}</span>
                  </span>
                  <span className="channel-actions">
                    <button
                      type="button"
                      className={`favorite-button ${isFavorite ? "is-favorite" : ""}`}
                      title={isFavorite ? "Remove favorite" : "Add favorite"}
                      aria-label={isFavorite ? `Remove ${channel.name} from favorites` : `Add ${channel.name} to favorites`}
                      onClick={(event) => {
                        event.stopPropagation();
                        onToggleFavorite(channel.id);
                      }}
                    >
                      <Star aria-hidden="true" size={15} />
                    </button>
                    <Play aria-hidden="true" size={15} />
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
